import { QueryErrorResetBoundary, useQuery } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import CogIcon from '@/assets/icons/cog.svg';
import PlusIcon from '@/assets/icons/plus-sm.svg';
import { LockClosedIcon } from '@/components/icons/lock-closed';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useAddContractContext } from '@/contexts/add-contract-context';
import { useIndeedPanelParams } from '@/hooks/use-indeed-panel-params';
import {
  indeedSettingsQueryOptions,
  useIndeedSettings,
} from '@/hooks/use-indeed-settings';
import { formatShortDate } from '@/utils/date-util';
import { reportBoundaryError } from '@/utils/sentry';

import {
  hasLinkedInContract,
  isPopularIndeedVisible,
  usePopularLinkedInContract,
  vonqContractsQueryOptions,
} from './popular-contracts';

function ContractCard({
  board,
  children,
}: {
  board: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={board}
      className="border-fx-neutral-200 flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-md border bg-white p-4 sm:flex-nowrap"
    >
      <ChannelIcon
        channelSlug={board.toLowerCase()}
        channelName={board}
        variant="large"
        size="sm"
      />
      {/* Cards share the page width, so wrap whole groups before squeezing metadata. */}
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-3 sm:flex-1">
        {children}
      </div>
    </section>
  );
}

function CardBoundary({
  board,
  children,
}: {
  board: string;
  children: ReactNode;
}) {
  const intl = useIntl();
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onError={reportBoundaryError(`settings.popular-contract.${board}`)}
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <ContractCard board={board}>
              <ErrorState
                message={intl.formatMessage(
                  {
                    defaultMessage:
                      "We couldn't load your {board} connection. Refresh the page or try again.",
                    description:
                      'A job board connection could not load; the employer can refresh or retry',
                  },
                  { board },
                )}
                onRetry={resetErrorBoundary}
              />
            </ContractCard>
          )}
        >
          <Suspense
            fallback={
              <ContractCard board={board}>
                <Skeleton className="h-4 w-40 rounded-full" />
                <Skeleton className="h-9 w-28" />
              </ContractCard>
            }
          >
            {children}
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

function IndeedCard({ locked }: { locked: boolean }) {
  const { data: settings } = useIndeedSettings();
  const { open } = useIndeedPanelParams();
  if (!isPopularIndeedVisible(settings, locked)) return null;

  return (
    <ContractCard board="Indeed">
      {settings.indeed_authorized && (
        <div className="max-w-full min-w-0 shrink-0 text-right [overflow-wrap:break-word]">
          <Typography variant="body1">
            {settings.indeed_authorization_email}
          </Typography>
          {settings.connected_by && (
            <Typography variant="small1" className="text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="Connected by {name}"
                description="The user who connected the employer’s Indeed account"
                values={{ name: settings.connected_by }}
              />
            </Typography>
          )}
          {settings.connected_at && (
            <Typography variant="small1" className="text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="Connected on {date}"
                description="Date the employer connected their Indeed account"
                values={{ date: formatShortDate(settings.connected_at) }}
              />
            </Typography>
          )}
        </div>
      )}
      <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-3">
        {locked && !settings.indeed_authorized && (
          <Tag
            className="bg-fx-green-50"
            textClassName="text-fx-green-800 text-caption font-medium tracking-[0.8px] uppercase"
            icon={<CheckIcon className="text-fx-green size-3" />}
            text={
              <FormattedMessage
                defaultMessage="Included"
                description="Indeed connection is included on the free Source plan"
              />
            }
          />
        )}
        <Button
          variant="ghost"
          className="shrink-0 px-2"
          onClick={() => open()}
        >
          {settings.indeed_authorized ? (
            <span
              aria-hidden="true"
              className="bg-fx-primary mask-contain mask-center mask-no-repeat size-4"
              style={{ maskImage: `url(${JSON.stringify(CogIcon)})` }}
            />
          ) : (
            <img src={PlusIcon} alt="" className="size-4" />
          )}
          {settings.indeed_authorized ? (
            <FormattedMessage
              defaultMessage="Configure"
              description="Action to change the connected Indeed account’s settings"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Connect now"
              description="Action to connect a job board account to Fountain"
            />
          )}
        </Button>
        {settings.indeed_authorized && (
          <Tag
            className="bg-fx-green-50 shrink-0"
            textClassName="text-fx-green-800"
            icon={
              <span
                aria-hidden="true"
                className="bg-fx-green size-1 rounded-full"
              />
            }
            text={
              <FormattedMessage
                defaultMessage="Connected"
                description="The employer’s Indeed account is connected to Fountain"
              />
            }
          />
        )}
      </div>
    </ContractCard>
  );
}

function LinkedInCard() {
  const channel = usePopularLinkedInContract();
  const { openWithChannel } = useAddContractContext();
  if (!channel) return null;
  return (
    <ContractCard board="LinkedIn">
      <Button
        variant="ghost"
        onClick={() => openWithChannel(channel.id, channel.name)}
      >
        <img src={PlusIcon} alt="" />
        <FormattedMessage
          defaultMessage="Connect now"
          description="Action to connect a job board account to Fountain"
        />
      </Button>
    </ContractCard>
  );
}

export function SourcePremiumTag() {
  return (
    <Tag
      className="bg-fx-amber-50 shrink-0"
      textClassName="text-fx-amber-800 text-caption font-medium tracking-[0.8px] uppercase"
      icon={<LockClosedIcon className="text-fx-amber-800 size-3" />}
      text={
        <FormattedMessage
          defaultMessage="SOURCE PREMIUM"
          description="Label on a badge marking a feature as available only to accounts that subscribe to the Source product"
        />
      }
    />
  );
}

export function PopularContractCards({
  locked = false,
  action,
}: {
  locked?: boolean;
  action?: ReactNode;
}) {
  // Observe the cards' existing caches without suspending the whole section.
  // Keep Paid reads active while hidden so unlinking can reveal a card again.
  const { data: settings } = useQuery({
    ...indeedSettingsQueryOptions,
    enabled: !locked,
  });
  const { data: contracts } = useQuery({
    ...vonqContractsQueryOptions,
    enabled: !locked,
  });
  if (
    !isPopularIndeedVisible(settings, locked) &&
    hasLinkedInContract(contracts?.contracts)
  )
    return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Typography variant="medium2" as="h2">
          <FormattedMessage
            defaultMessage="Popular contracts"
            description="Frequently used job board contracts available to connect"
          />
        </Typography>
        {action}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CardBoundary board="Indeed">
          <IndeedCard locked={locked} />
        </CardBoundary>
        {locked ? (
          <ContractCard board="LinkedIn">
            <SourcePremiumTag />
          </ContractCard>
        ) : (
          <CardBoundary board="LinkedIn">
            <LinkedInCard />
          </CardBoundary>
        )}
      </div>
    </section>
  );
}
